<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    filterCities,
    getCityList,
    getCountryList,
    markerStyles,
    type City,
    type Country,
    type MapEngine,
    type MapClickEvent,
  } from '@atlasguess/engine';
  import { createAudio, type AudioPlayer } from '@atlasguess/audio';
  import {
    applyEngineConfig,
    buildCityDailyShareContent,
    buildCityPrompt,
    cityDailyScore,
    formatLocalDateLabel,
    getTodaysCityDailyResult,
    haversineKm,
    localDateString,
    pickDailyCities,
    saveCityDailyResult,
    verdictForKm,
    type CityDailyConfig,
    type CityDailyRoundResult,
  } from '@atlasguess/games';
  import Prompt from '../framework/Prompt.svelte';
  import NextButton from '../framework/NextButton.svelte';
  import RoundCounter from '../framework/RoundCounter.svelte';
  import EndScreen from '../framework/EndScreen.svelte';
  import { displayUnit } from '../../lib/units';
  import { soundEnabled } from '../../lib/sound';
  import { recordRun, fetchDailyStatus } from '../../lib/runs';
  import { user } from '../../lib/stores/auth';
  import { get } from 'svelte/store';

  interface Props {
    config: CityDailyConfig;
    engine: MapEngine;
  }

  let { config, engine }: Props = $props();

  type Phase = 'loading' | 'awaiting-guess' | 'showing-result' | 'finished';

  let phase = $state<Phase>('loading');
  let countriesByIso = $state<Map<string, Country>>(new Map());

  /** All N cities, pre-picked deterministically from today's seed. */
  let plannedCities = $state<City[]>([]);
  /** Per-round results in play order — feeds the share grid + end screen. */
  let results = $state<CityDailyRoundResult[]>([]);
  /** 0-indexed pointer into plannedCities. */
  let roundIndex = $state(0);
  let lastDistanceKm = $state<number | null>(null);
  /**
   * Verdict locked at click time (not derived) so the post-guess panel
   * doesn't reroll its phrase on every reactive re-render. Reset to ''
   * between rounds; populated in handleMapClick.
   */
  let lastVerdict = $state('');

  let audio: AudioPlayer | undefined;
  let restoreEngineConfig: (() => void) | null = null;

  const CLICK_MARKER_ID = '_mode_click';
  const LOCATION_MARKER_ID = '_mode_location';
  const MARKER_LAYER = '_mode_round';

  /** Daily date helpers — computed once at mount, reused for share + storage. */
  const todayString = localDateString();
  const dateLabel = formatLocalDateLabel();

  /** Where to navigate on "Go to Practice Mode". Same scope, practice format. */
  const PRACTICE_HREF = '/play/cities-world/practice';

  const currentCity = $derived(plannedCities[roundIndex] ?? null);

  const prompt = $derived(
    currentCity ? buildCityPrompt(currentCity, countriesByIso) : null,
  );

  // Daily reads the post-guess result as a SCORE (0–100), not a distance.
  // Score is the displayed surface for every daily-game readout — result
  // panel, end screen, share text, leaderboard, stats.
  const resultText = $derived(
    lastDistanceKm === null ? '' : `Score ${cityDailyScore(lastDistanceKm)}`,
  );

  const isLastRound = $derived(roundIndex >= config.rounds - 1);

  /**
   * Bare site origin for the share link. Daily lives on `/`, so a
   * recipient who clicks lands on today's puzzle for them automatically
   * (date is local-resolved per user, so they see THEIR today).
   */
  const siteUrl = $derived.by((): string => {
    if (typeof window === 'undefined') return '';
    return window.location.origin;
  });

  /** Rich + plain share content — pulled from results (not the picker)
   *  so the displayed grid stays in sync with what the user actually saw. */
  const shareContent = $derived(
    buildCityDailyShareContent(
      results.map((r) => r.distanceKm),
      dateLabel,
      config.unitSystem,
      siteUrl,
    ),
  );

  /**
   * Per-round display info for the EndScreen's breakdown table —
   * the formatted city line for each played round, parallel to
   * `results`. We resolve cityId → City via plannedCities (always
   * available; deterministic from today's seed) and then run it
   * through buildCityPrompt so the same formatting rules used during
   * the gameplay prompt apply here too (US cities get state suffix,
   * etc.).
   */
  const roundDetails = $derived.by((): { primary: string }[] => {
    const citiesById = new Map(plannedCities.map((c) => [c.id, c]));
    return results.map((r) => {
      const city = citiesById.get(r.cityId);
      if (!city) return { primary: r.cityId };
      const p = buildCityPrompt(city, countriesByIso);
      return { primary: p.primary };
    });
  });

  onMount(() => {
    // Gate the AudioContext on the user's saved setting at construction
    // time — a muted user shouldn't pay for context init on first click.
    audio = createAudio({ enabled: $soundEnabled });
    restoreEngineConfig = applyEngineConfig(engine, config.layers);
    engine.setOnMapClick(handleMapClick);
    void initialize();
  });

  // Live-track the user's sound setting so toggling it in /account
  // silences any in-flight sequence and stops future ones immediately.
  $effect(() => {
    audio?.setEnabled($soundEnabled);
  });

  async function initialize(): Promise<void> {
    // Borders fetched alongside cities/countries — skip-fills + skip-hit-tester
    // because city-distance modes only need the borders mesh for the
    // post-guess fade-in overlay, not for tap resolution.
    const [cities, countries] = await Promise.all([
      getCityList({ tierMax: config.tierMax }),
      getCountryList(),
      engine.loadBorders({ buildFills: false, buildHitTester: false }),
    ]);
    countriesByIso = new Map(countries.map((c) => [c.iso_a2, c]));
    const filtered = filterCities(cities, {
      ...config.cityFilter,
      countryByIso: countriesByIso,
    });
    if (filtered.length < config.rounds) {
      console.warn('[atlas] daily pool smaller than required rounds; mode cannot start');
      return;
    }

    // Seeded daily picker — same five picks every time today's date
    // is the seed, so all players in all timezones see the same
    // puzzle. Past dates reproduce identically forever.
    //
    // Two heuristics layered on uniform random:
    //   1. Within-day country diversity — once today's slate has a
    //      city in country X, further candidates from X are 10×
    //      less likely (so a 100-city-China + 90-city-India pool
    //      doesn't routinely produce all-China days).
    //   2. Cross-day cooldown — cities picked in the past 14 days
    //      are 20× less likely (so Tokyo two days running becomes a
    //      real eyebrow-raiser rather than routine coincidence).
    //
    // Both penalties default to the values exported from
    // DEFAULT_DAILY_PICKER_WEIGHTS in @atlasguess/games — single
    // source of truth, easy to tune without touching this file.
    plannedCities = pickDailyCities({
      pool: filtered,
      count: config.rounds,
      date: todayString,
      idOf: (c) => c.id,
      countryOf: (c) => c.country_iso,
    });

    // If the user already played today, jump straight to the end screen
    // with their stored results. Authed users read from the DB so a
    // fresh login on a browser with a stale localStorage entry from a
    // previous account doesn't bleed in someone else's run; anonymous
    // users fall back to localStorage as before.
    const dailyRunKey = `${config.id}-${todayString}`;
    const completedRounds = get(user)
      ? await loadAuthedDailyRounds(dailyRunKey)
      : loadLocalDailyRounds();

    if (completedRounds && completedRounds.length === config.rounds) {
      results = completedRounds;
      roundIndex = config.rounds; // past the last round
      phase = 'finished';
      return;
    }

    startRound(0);
  }

  async function loadAuthedDailyRounds(
    runKey: string,
  ): Promise<CityDailyRoundResult[] | null> {
    const status = await fetchDailyStatus(config.id, runKey);
    if (!status?.completed) return null;
    return status.rounds.map((r) => ({
      cityId: r.target_id,
      distanceKm: r.score,
    }));
  }

  function loadLocalDailyRounds(): CityDailyRoundResult[] | null {
    const stored = getTodaysCityDailyResult(config.id);
    return stored ? stored.rounds : null;
  }

  function startRound(index: number): void {
    engine.markers.clear(MARKER_LAYER);
    engine.arcs.clear();
    // Hide borders for the prompt phase — see ClickDistancePractice for
    // the rationale (no shape-spoilers before the guess lands).
    engine.layers.outline.setBordersOpacity(0);
    audio?.cancel();
    lastDistanceKm = null;
    lastVerdict = '';
    roundIndex = index;
    phase = 'awaiting-guess';
  }

  function handleMapClick(event: MapClickEvent): void {
    if (phase !== 'awaiting-guess' || !currentCity || !audio) return;
    const guess = { lat: event.lat, lon: event.lon };
    const target = { lat: currentCity.lat, lon: currentCity.lon };
    const km = haversineKm(guess, target);
    lastDistanceKm = km;
    // Lock the verdict once per click — re-deriving from km would reroll
    // the phrase on every state update, which feels jittery.
    lastVerdict = verdictForKm(km);
    phase = 'showing-result';

    // Append to results array — drives the share grid + persistence.
    results = [...results, { cityId: currentCity.id, distanceKm: km }];

    engine.markers.add({
      id: CLICK_MARKER_ID,
      position: guess,
      style: markerStyles.click,
      layer: MARKER_LAYER,
    });
    engine.markers.add({
      id: LOCATION_MARKER_ID,
      position: target,
      style: markerStyles.location,
      layer: MARKER_LAYER,
    });

    // Reveal country borders so the player can see if they at least hit
    // the right country. Auto-animated tween (~400ms).
    engine.layers.outline.setBordersOpacity(1.0);

    const durationMs = config.audio.travelDurationMsForKm(km);
    const dingFrequencies = config.audio.dingFrequenciesForKm(km);
    audio.playSequence({ durationMs, dingFrequencies });
    engine.arcs.draw({
      from: guess,
      to: target,
      durationMs,
      removeOnComplete: false,
    });
  }

  function advance(): void {
    if (isLastRound) {
      // Persist the run before flipping to the end screen so a refresh
      // on the end screen reads the saved data, not a fresh start.
      saveCityDailyResult(config.id, results);
      // Fire-and-forget account stats write. run_key is the per-day id so
      // a replay/refresh of today's puzzle is idempotent server-side.
      void recordRun({
        mode_id: config.id,
        format: 'daily',
        run_key: `${config.id}-${todayString}`,
        rounds: results.map((r) => ({
          target_id: r.cityId,
          score: r.distanceKm,
          score_unit: 'km',
        })),
      });
      engine.markers.clear(MARKER_LAYER);
      engine.arcs.clear();
      audio?.cancel();
      phase = 'finished';
      return;
    }
    startRound(roundIndex + 1);
  }

  onDestroy(() => {
    engine.setOnMapClick(undefined);
    engine.markers.clear(MARKER_LAYER);
    engine.arcs.clear();
    audio?.cancel();
    audio?.dispose();
    restoreEngineConfig?.();
  });
</script>

{#if phase === 'awaiting-guess' || phase === 'showing-result'}
  <RoundCounter current={roundIndex + 1} total={config.rounds} />
{/if}

{#if (phase === 'awaiting-guess' || phase === 'showing-result') && prompt}
  <Prompt
    primary={prompt.primary}
    secondary={prompt.secondary}
    secondaryFlagUrl={prompt.flagFile ? `/${prompt.flagFile}` : undefined}
    active={phase === 'awaiting-guess'}
  />
{/if}

{#if phase === 'showing-result'}
  <div class="round-result round-result--card">
    <div class="round-result__distance">{resultText}</div>
    <div class="round-result__verdict">{lastVerdict}</div>
    <NextButton
      onclick={advance}
      label={isLastRound ? 'See results' : 'Next round'}
      spaceHotkey
    />
  </div>
{/if}

{#if phase === 'finished'}
  <EndScreen
    rounds={results}
    {roundDetails}
    {dateLabel}
    {shareContent}
    unitSystem={$displayUnit}
    practiceHref={PRACTICE_HREF}
  />
{/if}
